// This file is automatically generated. Do not edit it directly.
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const SUPABASE_KEY = SUPABASE_ANON_KEY || SUPABASE_PUBLISHABLE_KEY;

const isValidUrl = (value?: string) => {
  if (!value) return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

export const isSupabaseConfigured =
  isValidUrl(SUPABASE_URL) && Boolean(SUPABASE_KEY);

const createNoopClient = () => {
  const error = new Error(
    'Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.'
  );
  const createQuery = () => {
    const result = Promise.resolve({ data: null, error });
    const query: Record<string, unknown> & PromiseLike<{ data: null; error: Error }> = {
      select: () => query,
      order: () => query,
      limit: () => query,
      eq: () => query,
      in: () => query,
      insert: () => query,
      upsert: () => query,
      update: () => query,
      delete: () => query,
      single: () => query,
      maybeSingle: () => query,
      then: result.then.bind(result),
      catch: result.catch.bind(result),
      finally: result.finally.bind(result),
    };
    return query;
  };

  const noop = async () => ({ data: null, error });

  return {
    from: () => createQuery(),
    auth: {
      getSession: async () => ({ data: { session: null }, error }),
      getUser: async () => ({ data: { user: null }, error }),
      signOut: async () => ({ error }),
      signInWithPassword: async () => ({ data: { user: null, session: null }, error }),
      signInWithOtp: async () => ({ data: { user: null, session: null }, error }),
      exchangeCodeForSession: async () => ({ data: { session: null }, error }),
      onAuthStateChange: () => ({
        data: { subscription: { unsubscribe: () => {} } },
      }),
    },
    rpc: noop,
    functions: {
      invoke: noop,
    },
  } as unknown as SupabaseClient<Database>;
};

if (!isSupabaseConfigured) {
  console.warn(
    'Supabase client disabled because VITE_SUPABASE_URL or VITE_SUPABASE_PUBLISHABLE_KEY is missing.'
  );
}

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = isSupabaseConfigured
  ? createClient<Database>(SUPABASE_URL, SUPABASE_KEY, {
      auth: {
        storage: localStorage,
        persistSession: true,
        autoRefreshToken: true,
      },
    })
  : createNoopClient();
